// SidePanel (round 2): a thing opened beside what it came from, on round 1's panel Layer: shaded and
// cast so it reads as a layer over the page, Close at its top right, Escape closes it (BL-158 10). Its
// first line is the thing itself (a visit's window and customer), never a heading that names it; under
// it, a muted line and, when something's wrong, one red line; then its fields; one primary action; the
// rest as links. It sits at the right of the page, over what it came from. At phone widths its primary
// and its links are touch targets, 44 tall (the library's touch size). Reads only --panel-*; the layer
// reads its own.
// Given an `anchor` (Amir, 26 Oct), its top lines up with what it opened from (a visit's block), else
// with the top of its region (the board's time scale), inside the viewport (panel-place.ts), and clear of
// that block where it would sit over it (a phone, QA-878): placed as
// it opens, and again as the page scrolls or its own height changes, so it follows its block and stays
// in view. Without one it sits at --panel-top. Wide (round 2, a record's detail) is docked instead: the
// window's height at the right edge, so it ignores an anchor.
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '../Button/Button.tsx';
import { Layer } from '../Layer/Layer.tsx';
import { Link } from '../Link/Link.tsx';
import { tokenPx } from '../token-value.ts';
import { panelTop } from './panel-place.ts';
import './SidePanel.css';

export interface PanelAction {
  label: string;
  onPress?: () => void;
  href?: string;
}

/** What a panel lines its top up with: what it opened from, and the region it belongs to. */
export interface PanelAnchor {
  from: Element | null;
  within: Element | null;
}

export interface SidePanelProps {
  /** For screen readers: "The 10 a.m. visit at Marsh Dental". */
  label: string;
  /** Its first line: the thing itself ("10–12:30 p.m., Marsh Dental"). */
  title: string;
  /** One muted line under it ("Sam Rivera, working since 10:42 a.m."). */
  sub?: string;
  /** What's wrong, in red ("20 min over: the 1:15 at Chen residence will start late"). */
  note?: string | null;
  /** Its fields (KeyValue). */
  children?: ReactNode;
  /** The one thing to do next; none when there's nothing (a done visit). */
  primary?: PanelAction | null;
  links?: PanelAction[];
  onClose: () => void;
  isOpen?: boolean;
  /** Read as it's placed: what its top lines up with. Absent: --panel-top. Ignored when wide (docked). */
  anchor?: () => PanelAnchor;
  /** Round 2: a record's detail is wide (--sheet-w-wide), for its tables: docked, the window's height. */
  width?: 'default' | 'wide';
  /** Round 2: opened from a row, it takes the focus and gives it back on close. */
  takesFocus?: boolean;
  /** Round 2: above the first line, small: "‹ Back to visit 2" after a link inside replaced it. */
  before?: ReactNode;
  /** Round 2: after its actions, last: a record's Earlier and Later. */
  after?: ReactNode;
}

export function SidePanel({
  label,
  title,
  sub,
  note,
  children,
  primary,
  links = [],
  onClose,
  isOpen = true,
  anchor,
  width,
  takesFocus,
  before,
  after,
}: SidePanelProps) {
  const box = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState<number | null>(null);
  // docked (wide) runs the window's height at the right edge, so it has nothing to line up with
  const docked = width === 'wide';
  const read = useRef(docked ? undefined : anchor);
  read.current = docked ? undefined : anchor;
  const place = useRef(() => {});
  place.current = () => {
    const el = box.current;
    const a = read.current?.();
    if (!el || !a) return setTop(null);
    const f = a.from?.getBoundingClientRect();
    const own = el.getBoundingClientRect();
    const next = panelTop({
      from: f?.top ?? null,
      // over it across (a phone, where the panel spans the width): its foot, so it moves clear (QA-878)
      fromFoot: f && f.left < own.right && f.right > own.left ? f.bottom : null,
      within: a.within?.getBoundingClientRect().top ?? null,
      height: el.offsetHeight,
      viewport: window.innerHeight,
      margin: tokenPx('--panel-top', el),
    });
    setTop((t) => (t === next ? t : next));
  };
  // after each draw (a new visit, a re-read board), then whenever the page scrolls or it changes size
  useLayoutEffect(() => place.current());
  const anchored = !!anchor && !docked && isOpen;
  useEffect(() => {
    if (!anchored) return;
    const again = () => place.current();
    window.addEventListener('scroll', again, true);
    window.addEventListener('resize', again);
    const seen = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(again);
    if (box.current) seen?.observe(box.current);
    return () => {
      window.removeEventListener('scroll', again, true);
      window.removeEventListener('resize', again);
      seen?.disconnect();
    };
  }, [anchored]);
  return (
    <div
      ref={box}
      className="fl-panel-at"
      hidden={!isOpen}
      data-width={docked ? 'wide' : undefined}
      {...(top !== null ? { style: { insetBlockStart: top } } : {})}
    >
      <Layer
        level="panel"
        label={label}
        isOpen={isOpen}
        onClose={onClose}
        width={width}
        takesFocus={takesFocus}
      >
        <div className="fl-panel">
          <div className="fl-panel__top">
            {before ? <div className="fl-panel__before">{before}</div> : null}
            <p className="fl-panel__title">{title}</p>
            {sub ? <p className="fl-panel__sub">{sub}</p> : null}
            {note ? <p className="fl-panel__note">{note}</p> : null}
          </div>
          {children}
          {primary || links.length ? (
            <div className="fl-panel__actions">
              {primary ? (
                <Button variant="primary" size="touch-on-phone" onPress={primary.onPress}>
                  {primary.label}
                </Button>
              ) : null}
              {links.length ? (
                <div className="fl-panel__links">
                  {links.map((l) => (
                    <Link key={l.label} href={l.href} onPress={l.onPress} size="touch-on-phone">
                      {l.label}
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          {after}
        </div>
      </Layer>
    </div>
  );
}
